import { useEffect, useState, useCallback } from "react";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../contexts/AuthContext";
import { ArrowLeft, RefreshCw, Monitor, Smartphone, Tablet } from "lucide-react";

const OWNER_EMAIL = process.env.REACT_APP_OWNER_EMAIL;

function computeStats(rows) {
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const total = rows.length;
  const today = rows.filter((r) => new Date(r.created_at) >= startOfToday).length;
  const week  = rows.filter((r) => new Date(r.created_at) >= new Date(startOfToday - 7  * 86400000)).length;
  const month = rows.filter((r) => new Date(r.created_at) >= new Date(startOfToday - 30 * 86400000)).length;

  // Last 30 days by date
  const byDay = {};
  for (let i = 29; i >= 0; i--) {
    const d = new Date(startOfToday - i * 86400000);
    byDay[d.toISOString().slice(0, 10)] = 0;
  }
  rows.forEach((r) => {
    const k = r.created_at.slice(0, 10);
    if (k in byDay) byDay[k]++;
  });
  const days = Object.entries(byDay);

  // Devices
  const devices = { desktop: 0, mobile: 0, tablet: 0 };
  rows.forEach((r) => { if (r.device_type in devices) devices[r.device_type]++; });

  // Referrers
  const refMap = {};
  rows.forEach((r) => {
    let label = "Direct";
    if (r.referrer) {
      try { label = new URL(r.referrer).hostname; } catch { label = r.referrer; }
    }
    refMap[label] = (refMap[label] || 0) + 1;
  });
  const topRefs = Object.entries(refMap).sort(([, a], [, b]) => b - a).slice(0, 6);

  return { total, today, week, month, days, devices, topRefs };
}

function StatCard({ label, value }) {
  return (
    <div className="bg-[#141414] border border-white/10 rounded-xl p-4">
      <div className="text-3xl font-bold tabular-nums">{value}</div>
      <div className="text-[10px] uppercase tracking-[0.15em] text-white/40 font-bold mt-1">{label}</div>
    </div>
  );
}

function BarChart({ days }) {
  const max = Math.max(...days.map(([, v]) => v), 1);
  return (
    <div className="flex items-end gap-px h-20">
      {days.map(([date, count]) => (
        <div
          key={date}
          className="flex-1 bg-[#007AFF]/50 hover:bg-[#007AFF] rounded-sm transition-colors cursor-default"
          style={{ height: `${Math.max((count / max) * 100, count > 0 ? 5 : 0)}%` }}
          title={`${date}: ${count} visit${count !== 1 ? "s" : ""}`}
        />
      ))}
    </div>
  );
}

const DEVICE_ICONS = { desktop: Monitor, mobile: Smartphone, tablet: Tablet };

export default function AnalyticsDashboard({ onBack }) {
  const { session } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: err } = await supabase
      .from("pageviews")
      .select("created_at, device_type, referrer");
    if (err) { setError(err.message); setLoading(false); return; }
    setStats(computeStats(data || []));
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  if (session?.user?.email?.toLowerCase() !== OWNER_EMAIL?.toLowerCase()) {
    return (
      <div className="h-dvh w-screen bg-[#0A0A0A] flex items-center justify-center text-white/40 text-sm">
        Access denied
      </div>
    );
  }

  return (
    <div className="h-dvh w-screen flex flex-col bg-[#0A0A0A] text-white">
      {/* Header */}
      <header className="h-16 bg-[#0D0D0D] border-b border-white/10 flex items-center gap-4 px-4 md:px-6 shrink-0">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-white/60 hover:text-white transition-colors text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        <span className="font-display text-xl uppercase tracking-tight">Analytics</span>
        <span className="text-xs text-white/30 hidden sm:inline">app.crownanalyzer.com</span>
        <button
          onClick={load}
          disabled={loading}
          className="ml-auto p-2 rounded-md hover:bg-white/5 text-white/50 hover:text-white transition-colors disabled:opacity-40"
          title="Refresh"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </button>
      </header>

      <div className="flex-1 overflow-y-auto p-4 md:p-6">
        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
            {error}
          </div>
        )}

        {loading && !stats && (
          <div className="h-full flex items-center justify-center">
            <div className="w-6 h-6 rounded-full border-2 border-white/20 border-t-white animate-spin" />
          </div>
        )}

        {stats && (
          <div className="max-w-3xl mx-auto space-y-4">
            {/* Stat cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatCard label="Total visits" value={stats.total} />
              <StatCard label="Today" value={stats.today} />
              <StatCard label="Last 7 days" value={stats.week} />
              <StatCard label="Last 30 days" value={stats.month} />
            </div>

            {/* Bar chart */}
            <div className="bg-[#141414] border border-white/10 rounded-xl p-4">
              <div className="text-[10px] uppercase tracking-[0.15em] text-white/40 font-bold mb-3">
                Daily visits — last 30 days
              </div>
              <BarChart days={stats.days} />
              <div className="flex justify-between text-[10px] text-white/30 mt-1.5">
                <span>{stats.days[0]?.[0]?.slice(5)}</span>
                <span>today</span>
              </div>
            </div>

            {/* Devices + Sources */}
            <div className="grid md:grid-cols-2 gap-3">
              <div className="bg-[#141414] border border-white/10 rounded-xl p-4">
                <div className="text-[10px] uppercase tracking-[0.15em] text-white/40 font-bold mb-3">
                  Devices
                </div>
                <div className="space-y-2.5">
                  {Object.entries(stats.devices).map(([type, count]) => {
                    const Icon = DEVICE_ICONS[type];
                    const pct = stats.total ? Math.round((count / stats.total) * 100) : 0;
                    return (
                      <div key={type} className="flex items-center gap-2">
                        <Icon className="w-4 h-4 text-white/40 shrink-0" />
                        <span className="text-xs capitalize text-white/60 w-14">{type}</span>
                        <div className="flex-1 h-1.5 bg-white/5 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-[#007AFF] rounded-full transition-all"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="text-xs font-mono text-white/40 w-8 text-right">{pct}%</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="bg-[#141414] border border-white/10 rounded-xl p-4">
                <div className="text-[10px] uppercase tracking-[0.15em] text-white/40 font-bold mb-3">
                  Top sources
                </div>
                <div className="space-y-2">
                  {stats.topRefs.length === 0 && (
                    <span className="text-xs text-white/30">No data yet</span>
                  )}
                  {stats.topRefs.map(([ref, count]) => (
                    <div key={ref} className="flex items-center justify-between gap-2">
                      <span className="text-xs text-white/70 truncate">{ref}</span>
                      <span className="text-xs font-mono text-white/40 shrink-0">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
